import React from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import PropTypes from "prop-types";
import { useState } from "react";
import CustomPagination from "./Pagination";

const GenericTable = ({
  headers,
  rows,
  renderCell,
  pagination,
  resultsPerPage,
  // External pagination props
  currentPage: externalCurrentPage,
  setCurrentPage: externalSetCurrentPage,
  onResultsPerPageChange,
  totalCount,
  useExternalPagination = false,
}) => {
  const [internalCurrentPage, setInternalCurrentPage] = useState(1);

  // Use external pagination state if provided, otherwise use internal state
  const currentPage = useExternalPagination
    ? externalCurrentPage
    : internalCurrentPage;
  const setCurrentPage = useExternalPagination
    ? externalSetCurrentPage
    : setInternalCurrentPage;

  // Calculate pagination based on whether we're using external or internal pagination
  const totalPages = useExternalPagination
    ? Math.ceil(totalCount / resultsPerPage)
    : Math.ceil(rows.length / resultsPerPage);

  const startIdx = useExternalPagination
    ? 0 // For external pagination, rows are already paginated from API
    : (currentPage - 1) * resultsPerPage;
  const endIdx = useExternalPagination
    ? rows.length // For external pagination, use all rows
    : startIdx + resultsPerPage;

  // For external pagination, use rows as-is (API should return paginated data)
  // For internal pagination, slice the rows based on current page
  const paginatedRows = useExternalPagination
    ? rows // For external pagination, use rows as-is
    : rows.slice(startIdx, endIdx);

  // Debug logging
  if (useExternalPagination) {
    console.log("External Pagination Debug:", {
      currentPage,
      resultsPerPage,
      totalCount,
      totalPages,
      rowsLength: rows.length,
      paginatedRowsLength: paginatedRows.length,
    });
  }

  return (
    <div className="border overflow-hidden   border-[#ECECEC] rounded-[12px] over">
      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 738 }}>
          <TableHead>
            <TableRow>
              {headers.map((header, idx) => (
                <TableCell
                  key={idx}
                  sx={{
                    fontWeight: 500,
                    background: "#F3F3F3",
                    fontSize: 16,
                    color: "#222",
                  }}
                >
                  {header}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedRows.map((row, rowIdx) => (
              <TableRow key={rowIdx}>
                {headers.map((header, colIdx) => (
                  <TableCell key={colIdx} sx={{ fontSize: 16, color: "#222" }}>
                    {renderCell ? renderCell(row, header, colIdx) : row[header]}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <CustomPagination
        padding={true}
        resultsPerPage={resultsPerPage}
        totalCount={useExternalPagination ? totalCount : rows.length}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        onResultsPerPageChange={
          useExternalPagination ? onResultsPerPageChange : undefined
        }
      />
    </div>
  );
};

GenericTable.propTypes = {
  headers: PropTypes.arrayOf(PropTypes.string).isRequired,
  rows: PropTypes.arrayOf(PropTypes.object).isRequired,
  renderCell: PropTypes.func, // (row, header, colIdx) => ReactNode
  pagination: PropTypes.node,
};

export default GenericTable;
